// Полоса панели проверки над сеткой: одна фраза, число и переход.
//
// Это единственный вопрос, ради которого экран открывают: «могу ли я закрывать период». Ответ
// обязан быть виден без единого нажатия, поэтому счётчики приезжают в том же ответе, что и
// сетка, а не отдельным запросом — посчитанные отдельно, они разойдутся с сеткой на любой
// параллельной правке, и человек перестанет верить обеим.
//
// Когда замечаний нет, это пишется СЛОВОМ, а не нулём в счётчике. Ноль читается как «не
// считали», а молчание — как «здесь ничего не проверяют». `nothing_to_check` существует в
// контракте отдельно от нулей ровно поэтому.

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import type { ReviewPanelOut } from '@/shared/api/endpoints/official';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import * as Button from '@/shared/ui/button';

export function ReviewBar({
  review,
  onOpen,
}: {
  review: ReviewPanelOut;
  /** Переход в разбор замечаний. */
  onOpen: () => void;
}) {
  if (review.nothing_to_check) {
    return (
      <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' />
        <span className='truncate text-paragraph-sm text-text-strong-950'>
          {/* Перечень стоял ПЕРЕД глаголом: «траншей…, переплат…, отказов…, строк… нет» —
              четыре вещи надо было удержать в голове, прежде чем узнать, что с ними. Строй
              «ни… ни… ни…» после ответа читается сразу, а сведений в нём столько же. */}
          Закрывать можно: ни непроведённых траншей, ни переплат, ни отказов от переноса, ни строк
          без реквизитов.
        </span>
      </div>
    );
  }

  // Единственный вопрос, ради которого открывают экран, — «могу ли я закрывать период».
  // Мешает закрытию РОВНО одно: непроведённые транши. Перенос остатка, переплаты и отказы —
  // штатные явления каждого месяца, и жёлтая «требует разбора до закрытия» на них висела бы
  // всегда, отвечая «нет» там, где ответ «да» (правило 10).
  const мешает = review.unpaid_tranches > 0;
  return (
    <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
      {мешает ? (
        <RiErrorWarningLine className='size-4 shrink-0 text-warning-base' />
      ) : (
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' />
      )}
      <span className='min-w-0 truncate text-paragraph-sm text-text-strong-950'>
        {мешает ? (
          <>
            Закрытию мешает: {countLabel(review.unpaid_tranches, ['транш', 'транша', 'траншей'])} не
            проведено на {formatKopecks(review.unpaid_total)}.
          </>
        ) : (
          <>
            Закрывать можно.
            <span className='pl-1 text-text-sub-600'>Стоит посмотреть: {review.summary}.</span>
          </>
        )}
      </span>
      <Button.Root
        variant='primary'
        mode='stroke'
        size='xxsmall'
        className='shrink-0'
        onClick={onOpen}
      >
        Разобрать
      </Button.Root>
    </div>
  );
}
